import React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/common/Button'
import { Toolbar } from '@/components/common/Toolbar'
import { PageHeader } from '@/components/common/PageHeader'
import { SectionTitle } from '@/components/common/SectionTitle'
import {
  useRelationships,
  type CreateRelationshipInput,
  type RelationshipClasificacion,
  type RelationshipListItem,
  type RelationshipPriority,
} from '@/hooks/useRelationships'
import { usePersonas } from '@/hooks/usePersonas'
import { useCommercialSignals } from '@/hooks/useCommercialSignals'
import './prototype.css'
import './hoy.css'

type ViewKey =
  | 'todos'
  | 'p1'
  | 'p2'
  | 'p3'
  | 'retail'
  | 'industria'
  | 'logistica'
  | 'activas'
  | 'conPersona'
  | 'sinPersona'
  | 'compromisos'
  | 'sinAccion'
  | 'contexto'
  | 'signals'

type PresentationMode = 'bloques' | 'tabla'
type CompanySortKey = 'empresa' | 'prioridad' | 'responsable' | 'estado' | 'personas' | 'proximo' | 'ultima'

const VIEW_LABEL: Record<ViewKey, string> = {
  todos: 'Todos',
  p1: 'P1',
  p2: 'P2',
  p3: 'P3',
  retail: 'Retail',
  industria: 'Industria',
  logistica: 'Logistica',
  activas: 'Activas',
  conPersona: 'Con persona',
  sinPersona: 'Sin persona',
  compromisos: 'Con compromiso',
  sinAccion: 'Sin siguiente acción',
  contexto: 'Sin contexto suficiente',
  signals: 'Con señales',
}

const VIEW_ORDER: ViewKey[] = [
  'todos',
  'p1',
  'p2',
  'p3',
  'retail',
  'industria',
  'logistica',
  'activas',
  'conPersona',
  'sinPersona',
  'compromisos',
  'sinAccion',
  'contexto',
  'signals',
]

const PRIMARY_VIEW_ORDER: ViewKey[] = ['todos', 'p1', 'p2', 'p3']
const SECONDARY_VIEW_ORDER = VIEW_ORDER.filter((key) => !PRIMARY_VIEW_ORDER.includes(key))
function isViewKey(value: string | null): value is ViewKey {
  return !!value && (VIEW_ORDER as string[]).includes(value)
}

function segmentoMatches(segmento: string | null, target: 'retail' | 'industria' | 'logistica'): boolean {
  return (segmento ?? '').trim().toLowerCase() === target
}

const estadoLabel: Record<RelationshipListItem['estadoRelacion'], string> = {
  activa: 'Activa',
  requiere_atencion: 'Se enfría',
  pausada: 'Pausada',
  cerrada: 'Cerrada',
}

const signalLabel: Record<RelationshipListItem['signal'], string> = {
  al_dia: 'Al día',
  requiere_atencion: 'Requiere atención',
  pausada: 'En pausa',
  sin_contexto_suficiente: 'Sin contexto suficiente',
}

const clasificacionLabel: Record<RelationshipClasificacion, string> = {
  prospecto: 'Prospecto',
  cliente: 'Cliente',
  socio: 'Socio',
  institucion: 'Institución',
  dormida: 'Dormida',
}

const CLASIFICACIONES: RelationshipClasificacion[] = [
  'prospecto',
  'cliente',
  'socio',
  'institucion',
  'dormida',
]

const prioridadLabel: Record<RelationshipPriority, string> = {
  p1: 'P1 · Prioridad alta',
  p2: 'P2 · Prioridad media',
  p3: 'P3 · Prioridad baja',
}

const PRIORIDADES: RelationshipPriority[] = ['p1', 'p2', 'p3']

function formatActivity(iso: string | null): string {
  if (!iso) return 'Sin contexto Microsoft asociado'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })
}

const NewRelationshipForm: React.FC<{
  onCreate: (input: CreateRelationshipInput) => Promise<unknown>
  onClose: () => void
}> = ({ onCreate, onClose }) => {
  const [title, setTitle] = React.useState('')
  const [clasificacion, setClasificacion] = React.useState<RelationshipClasificacion>('prospecto')
  const [prioridadComercial, setPrioridadComercial] = React.useState<RelationshipPriority | ''>('')
  const [pais, setPais] = React.useState('')
  const [dominios, setDominios] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    if (!title.trim()) return setError('El nombre de la relación es obligatorio.')
    if (!pais.trim()) return setError('El país es obligatorio.')
    setSubmitting(true)
    try {
      await onCreate({
        title: title.trim(),
        clasificacion,
        ...(prioridadComercial ? { prioridadComercial } : {}),
        pais: pais.trim(),
        dominios: dominios
          .split(',')
          .map((d) => d.trim())
          .filter(Boolean),
      })
      onClose()
    } catch {
      setError('No se pudo crear la relación. Reintenta en unos segundos.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form className="prototypeContextCard" onSubmit={submit} style={{ marginBottom: 16 }}>
      <SectionTitle title="Nueva relación" subtitle="Registra una organización para empezar a darle seguimiento." />
      <div className="prototypeRelationshipRow__grid">
        <label className="prototypeRelationshipRow__cell">
          <span className="prototypeMiniLabel">Nombre *</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Contoso S.A." autoFocus />
        </label>
        <label className="prototypeRelationshipRow__cell">
          <span className="prototypeMiniLabel">Prioridad comercial (opcional)</span>
          <select value={prioridadComercial} onChange={(e) => setPrioridadComercial(e.target.value as RelationshipPriority | '')}>
            <option value="">Sin priorizar</option>
            {PRIORIDADES.map((prioridad) => (
              <option key={prioridad} value={prioridad}>{prioridadLabel[prioridad]}</option>
            ))}
          </select>
          <small>Se define manualmente; Sales no la asigna por clasificación o estado.</small>
        </label>
        <label className="prototypeRelationshipRow__cell">
          <span className="prototypeMiniLabel">Clasificación *</span>
          <select value={clasificacion} onChange={(e) => setClasificacion(e.target.value as RelationshipClasificacion)}>
            {CLASIFICACIONES.map((c) => (
              <option key={c} value={c}>
                {clasificacionLabel[c]}
              </option>
            ))}
          </select>
        </label>
        <label className="prototypeRelationshipRow__cell">
          <span className="prototypeMiniLabel">País *</span>
          <input value={pais} onChange={(e) => setPais(e.target.value)} placeholder="CL" />
        </label>
        <label className="prototypeRelationshipRow__cell">
          <span className="prototypeMiniLabel">Dominio web (opcional)</span>
          <input value={dominios} onChange={(e) => setDominios(e.target.value)} placeholder="contoso.com" />
          <small>Úsalo para identificar la empresa. Si hay más de uno, sepáralos con coma.</small>
        </label>
      </div>
      {error && <p className="prototypeRelationshipRow__summary" role="alert">{error}</p>}
      <div className="prototypeContextCard__actions">
        <Button size="sm" type="submit" disabled={submitting}>
          {submitting ? 'Guardando…' : 'Crear relación'}
        </Button>
        <Button variant="secondary" size="sm" type="button" onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
      </div>
    </form>
  )
}

export const Relaciones: React.FC = () => {
  const { status, relationships, error, reload, create } = useRelationships()
  const { status: personasStatus, personas } = usePersonas()
  const commercialSignals = useCommercialSignals()
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = React.useState('')
  const initialView = isViewKey(searchParams.get('filtro')) ? (searchParams.get('filtro') as ViewKey) : 'todos'
  const [view, setView] = React.useState<ViewKey>(initialView)
  const [creating, setCreating] = React.useState(false)
  const [presentationMode, setPresentationMode] = React.useState<PresentationMode>('bloques')
  const [sort, setSort] = React.useState<{ key: CompanySortKey; direction: 'asc' | 'desc' }>({ key: 'prioridad', direction: 'asc' })

  const setViewAndUrl = (next: ViewKey) => {
    setView(next)
    setSearchParams(next === 'todos' ? {} : { filtro: next })
  }

  const orgIdsConPersona = React.useMemo(
    () => new Set(personas.map((p) => p.organizacionId).filter(Boolean) as string[]),
    [personas]
  )
  const personasPorOrg = React.useMemo(() => {
    const map = new Map<string, number>()
    for (const p of personas) {
      if (!p.organizacionId) continue
      map.set(p.organizacionId, (map.get(p.organizacionId) ?? 0) + 1)
    }
    return map
  }, [personas])
  const signalsPorOrg = React.useMemo(() => {
    const map = new Map<string, number>()
    for (const signal of commercialSignals.signals) map.set(signal.organizationId, (map.get(signal.organizationId) ?? 0) + 1)
    return map
  }, [commercialSignals.signals])

  const resumen = React.useMemo(() => {
    const sectores = { retail: 0, industria: 0, logistica: 0 }
    let conPersona = 0
    let sinPersona = 0
    let sinAccion = 0
    let conCompromiso = 0
    const prioridades: Record<RelationshipPriority, number> = { p1: 0, p2: 0, p3: 0 }
    for (const r of relationships) {
      if (segmentoMatches(r.segmento, 'retail')) sectores.retail += 1
      else if (segmentoMatches(r.segmento, 'industria')) sectores.industria += 1
      else if (segmentoMatches(r.segmento, 'logistica')) sectores.logistica += 1
      if (orgIdsConPersona.has(r.id)) conPersona += 1
      else sinPersona += 1
      if (r.nextAction) conCompromiso += 1
      else sinAccion += 1
      if (r.prioridadComercial) prioridades[r.prioridadComercial] += 1
    }
    const requierenAtencion = relationships.filter((relationship) =>
      ['requiere_atencion', 'sin_contexto_suficiente'].includes(relationship.signal),
    ).length
    return { total: relationships.length, sectores, prioridades, conPersona, sinPersona, sinAccion, conCompromiso, requierenAtencion }
  }, [relationships, orgIdsConPersona])

  const filtered = relationships.filter((r) => {
    const matchesSearch =
      !search ||
      r.title.toLowerCase().includes(search.toLowerCase()) ||
      (r.resumenActual ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (r.responsable ?? '').toLowerCase().includes(search.toLowerCase())

    let matchesView = true
    if (view === 'p1' || view === 'p2' || view === 'p3') matchesView = r.prioridadComercial === view
    else if (view === 'activas') matchesView = r.estadoRelacion === 'activa'
    else if (view === 'retail') matchesView = segmentoMatches(r.segmento, 'retail')
    else if (view === 'industria') matchesView = segmentoMatches(r.segmento, 'industria')
    else if (view === 'logistica') matchesView = segmentoMatches(r.segmento, 'logistica')
    else if (view === 'conPersona') matchesView = orgIdsConPersona.has(r.id)
    else if (view === 'sinPersona') matchesView = !orgIdsConPersona.has(r.id)
    else if (view === 'compromisos') matchesView = Boolean(r.nextAction)
    else if (view === 'sinAccion') matchesView = !r.nextAction
    else if (view === 'contexto') matchesView = r.signal === 'sin_contexto_suficiente'
    else if (view === 'signals') matchesView = (signalsPorOrg.get(r.id) ?? 0) > 0

    return matchesSearch && matchesView
  })

  const sorted = React.useMemo(() => {
    const valueFor = (relationship: RelationshipListItem): string | number => {
      switch (sort.key) {
        case 'prioridad': return relationship.prioridadComercial === 'p1' ? 1 : relationship.prioridadComercial === 'p2' ? 2 : relationship.prioridadComercial === 'p3' ? 3 : 4
        case 'responsable': return relationship.responsable ?? ''
        case 'estado': return estadoLabel[relationship.estadoRelacion]
        case 'personas': return personasPorOrg.get(relationship.id) ?? 0
        case 'proximo': return relationship.nextAction ?? ''
        case 'ultima': return relationship.lastActivity ?? ''
        default: return relationship.title
      }
    }
    return [...filtered].sort((left, right) => {
      const a = valueFor(left)
      const b = valueFor(right)
      const comparison = typeof a === 'number' && typeof b === 'number'
        ? a - b
        : String(a).localeCompare(String(b), 'es', { sensitivity: 'base' })
      return sort.direction === 'asc' ? comparison : -comparison
    })
  }, [filtered, personasPorOrg, sort])

  const toggleSort = (key: CompanySortKey) => {
    setSort((current) => current.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' })
  }

  const sortLabel = (key: CompanySortKey, label: string) => (
    <button type="button" className="prototypeListTable__sort" onClick={() => toggleSort(key)}>
      {label}{sort.key === key ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : ''}
    </button>
  )

  const companyCard = (relationship: RelationshipListItem, mobile = false) => (
    <article
      key={`${mobile ? 'mobile-' : ''}${relationship.id}`}
      className={`prototypeRelationshipRow commercialCompanyCard${mobile ? ' commercialCompanyCard--mobile' : ''}`}
    >
      <div className="prototypeRelationshipRow__topline">
        <div className="prototypeRelationshipRow__titleBlock">
          <Link to={`/relaciones/${relationship.id}`} className="prototypeRelationshipRow__title">
            {relationship.title}
          </Link>
          <span className="prototypeRelationshipRow__responsible">{relationship.responsable ?? 'Sin responsable asignado'}</span>
        </div>
        <Link to={`/relaciones/${relationship.id}`} className="commercialPrimaryLink">
          Abrir contexto
        </Link>
      </div>
      <div className="prototypeRelationshipRow__signals">
        <span className="prototypeOriginMark prototypeOriginMark--relationship">{signalLabel[relationship.signal]}</span>
        {relationship.prioridadComercial && <span className="empresasChip">{relationship.prioridadComercial.toUpperCase()}</span>}
        <span>{estadoLabel[relationship.estadoRelacion]}</span>
        {(signalsPorOrg.get(relationship.id) ?? 0) > 0 && <span className="commercialSignalBadge">{signalsPorOrg.get(relationship.id)} señal{signalsPorOrg.get(relationship.id) === 1 ? '' : 'es'}</span>}
        <span>{personasPorOrg.get(relationship.id) ?? 0} persona{(personasPorOrg.get(relationship.id) ?? 0) === 1 ? '' : 's'}</span>
      </div>
      {relationship.resumenActual && <p className="prototypeRelationshipRow__summary">{relationship.resumenActual}</p>}
      <div className="commercialDecisionGrid">
        <div>
          <span className="prototypeMiniLabel">Última actividad válida</span>
          <strong>{relationship.lastActivity ? formatActivity(relationship.lastActivity) : 'Sin actividad verificable'}</strong>
        </div>
        <div>
          <span className="prototypeMiniLabel">Siguiente acción</span>
          <strong>{relationship.nextAction ?? 'Definir próxima acción'}</strong>
        </div>
      </div>
    </article>
  )

  const renderBody = () => {
    if (status === 'loading') {
      return (
        <div className="commercialLoadingState" role="status" aria-live="polite" aria-busy="true">
          <strong>Preparando la bandeja comercial…</strong>
          <span />
          <span />
          <span />
        </div>
      )
    }
    if (status === 'not_signed_in') {
      return <p className="prototypeRelationshipRow__summary">Inicia sesión con tu cuenta Microsoft para ver tus relaciones.</p>
    }
    if (status === 'not_configured') {
      return (
        <p className="prototypeRelationshipRow__summary">
          La conexión con SharePoint aún no está habilitada en este entorno. Las relaciones aparecerán aquí en cuanto se
          configure el acceso de la aplicación.
        </p>
      )
    }
    if (status === 'forbidden') {
      return (
        <p className="prototypeRelationshipRow__summary">
          Tu identidad es válida pero tu perfil aún no está habilitado en Wacner Sales. Solicita habilitación a un
          administrador.
        </p>
      )
    }
    if (status === 'unauthorized' || status === 'error') {
      return (
        <div>
          <p className="prototypeRelationshipRow__summary">
            No se pudieron cargar las relaciones{error instanceof Error ? `: ${error.message}` : '.'}
          </p>
          <Button size="sm" variant="secondary" onClick={reload}>
            Reintentar
          </Button>
        </div>
      )
    }
    if (filtered.length === 0) {
      return (
        <p className="prototypeRelationshipRow__summary">
          No hay relaciones que coincidan. {relationships.length === 0 ? 'Crea la primera con “Nueva relación”.' : 'Ajusta el filtro o la búsqueda.'}
        </p>
      )
    }

    if (presentationMode === 'tabla') {
      return (
        <>
          <div className="prototypeListTable__wrap commercialDesktopOnly">
            <table className="prototypeListTable">
              <thead>
                <tr>
                  <th>{sortLabel('empresa', 'Empresa')}</th>
                  <th>{sortLabel('prioridad', 'Prioridad')}</th>
                  <th>{sortLabel('responsable', 'Responsable')}</th>
                  <th>{sortLabel('estado', 'Estado')}</th>
                  <th>{sortLabel('personas', 'Personas')}</th>
                  <th>{sortLabel('proximo', 'Próximo compromiso')}</th>
                  <th>{sortLabel('ultima', 'Última interacción')}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((relationship) => (
                  <tr key={relationship.id}>
                    <td><Link to={`/relaciones/${relationship.id}`} className="prototypeListTable__primary">{relationship.title}</Link>{(signalsPorOrg.get(relationship.id) ?? 0) > 0 && <> <span className="commercialSignalBadge">{signalsPorOrg.get(relationship.id)}</span></>}</td>
                    <td>{relationship.prioridadComercial ? <span className="prototypeOriginMark prototypeOriginMark--relationship">{relationship.prioridadComercial.toUpperCase()}</span> : 'Sin priorizar'}</td>
                    <td>{relationship.responsable ?? 'Sin responsable'}</td>
                    <td><span className="prototypeOriginMark prototypeOriginMark--relationship">{estadoLabel[relationship.estadoRelacion]}</span></td>
                    <td>{personasPorOrg.get(relationship.id) ?? 0}</td>
                    <td>{relationship.nextAction ?? 'Sin compromiso abierto'}</td>
                    <td>{relationship.lastActivity ? formatActivity(relationship.lastActivity) : 'Sin interacción registrada'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="commercialMobileList">
            {sorted.map((relationship) => companyCard(relationship, true))}
          </div>
        </>
      )
    }

    return <div className="prototypeRelationshipTray commercialCompanyTray">{sorted.map((relationship) => companyCard(relationship))}</div>
  }

  const canCreate = status === 'ready' || status === 'forbidden' || filtered.length >= 0

  return (
    <div className="prototypePage">
      <PageHeader
        title="Empresas"
        subtitle="Señales M365 y próximas acciones, no una cartera comercial tradicional."
        action={
          <div className="prototypeContextCard__actions">
            <Button size="sm" variant="secondary" onClick={() => setCreating((v) => !v)} disabled={!canCreate}>
              {creating ? 'Cerrar' : 'Nueva relación'}
            </Button>
          </div>
        }
      />

      {creating && status !== 'not_configured' && status !== 'not_signed_in' && (
        <NewRelationshipForm onCreate={create} onClose={() => setCreating(false)} />
      )}

      {status === 'ready' && personasStatus === 'ready' && (
        <div className="empresasSummary">
          <button className="hoy__stat" onClick={() => setViewAndUrl('todos')}>
            <strong>{resumen.total}</strong>
            <span>Empresas</span>
          </button>
          <button className="hoy__stat" onClick={() => setViewAndUrl('p1')}>
            <strong>{resumen.prioridades.p1}</strong>
            <span>Prioridad P1</span>
          </button>
          <button className="hoy__stat" onClick={() => setViewAndUrl('contexto')}>
            <strong>{resumen.requierenAtencion}</strong>
            <span>Requieren atención</span>
          </button>
        </div>
      )}

      <div className="prototypeHero prototypeHero--list">
        <div className="prototypeHero__copy">
          <div className="prototypeHero__eyebrow">Bandeja de empresas</div>
          <div className="prototypeHero__date">Qué requiere atención ahora</div>
          <p className="prototypeHero__body">
            La vista prioriza empresas activas, relaciones que se enfrían, trabajo con compromisos y conversaciones
            recientes para decidir dónde poner energía hoy.
          </p>
        </div>
        <div className="prototypeHero__tagRow commercialPrimaryFilters">
          {PRIMARY_VIEW_ORDER.map((key) => (
            <button
              key={key}
              className={`prototypeFilter ${view === key ? 'prototypeFilter--active' : ''}`}
              onClick={() => setViewAndUrl(key)}
            >
              {VIEW_LABEL[key]}
            </button>
          ))}
        </div>
        <details className="commercialMoreFilters">
          <summary>Más filtros</summary>
          <div className="commercialMoreFilters__content">
            {SECONDARY_VIEW_ORDER.map((key) => (
              <button
                key={key}
                className={`prototypeFilter ${view === key ? 'prototypeFilter--active' : ''}`}
                onClick={() => setViewAndUrl(key)}
              >
                {VIEW_LABEL[key]}
              </button>
            ))}
          </div>
        </details>
      </div>

      <Toolbar searchPlaceholder="Buscar empresa, persona o señal..." onSearch={setSearch} />

      <section className="prototypeSection">
        <SectionTitle
          title={status === 'ready' ? `${filtered.length} empresas en seguimiento` : 'Empresas'}
          subtitle="Salud, última actividad válida y siguiente acción para decidir dónde intervenir."
          action={
            <div className="prototypeContextCard__actions" role="group" aria-label="Vista de empresas">
              <Button size="sm" variant={presentationMode === 'bloques' ? 'primary' : 'secondary'} onClick={() => setPresentationMode('bloques')}>Bloques</Button>
              <Button size="sm" variant={presentationMode === 'tabla' ? 'primary' : 'secondary'} onClick={() => setPresentationMode('tabla')}>Tabla</Button>
            </div>
          }
        />
        {renderBody()}
      </section>
    </div>
  )
}
